/**
 * Segmentación del calendario en semanas (con historial de inicio de semana,
 * FR-TIME-05) y meses.
 */
import {
  addDays,
  addMonths,
  alignBack,
  alignForward,
  endOfMonth,
  minDate,
  monthKey,
  startOfMonth,
  type LocalDate,
} from './dates.js';
import type { EngineSettings } from './types.js';

export interface Period {
  start: LocalDate;
  end: LocalDate;
  /** Longitud nominal usada para prorratear cuotas (7 en semanas, días del mes en meses). */
  nominal: number;
  transitional: boolean;
  key: string;
}

export function currentWeekStart(settings: EngineSettings): number {
  const last = settings.weekStartChanges[settings.weekStartChanges.length - 1];
  return last ? last.weekStart : settings.initialWeekStart;
}

/**
 * Semanas que se solapan con [from, to].
 * Regla: la semana en curso al cambiar el ajuste termina con la definición antigua;
 * los días hasta el siguiente inicio según la nueva configuración forman una UE
 * de transición (nominal 7, cuota prorrateada).
 */
export function weekPeriods(settings: EngineSettings, from: LocalDate, to: LocalDate): Period[] {
  const changes = [...settings.weekStartChanges].sort((a, b) => (a.changedOn < b.changedOn ? -1 : 1));
  const firstChange = changes[0]?.changedOn;
  const anchor = addDays(firstChange && firstChange < from ? firstChange : from, -14);
  let rule = settings.initialWeekStart;
  let idx = 0;
  let start = alignBack(anchor, rule);
  const out: Period[] = [];

  const takeChangesUpTo = (end: LocalDate): number | null => {
    let pending: number | null = null;
    while (idx < changes.length && changes[idx]!.changedOn <= end) {
      pending = changes[idx]!.weekStart;
      idx++;
    }
    return pending;
  };

  while (start <= to) {
    const end = addDays(start, 6);
    if (end >= from) out.push({ start, end, nominal: 7, transitional: false, key: `W:${start}` });
    let next = addDays(end, 1);
    let pending = takeChangesUpTo(end);
    if (pending !== null && pending !== rule) {
      // Puede haber más cambios durante la transición: se recalcula hasta estabilizar.
      for (;;) {
        const newStart = alignForward(next, pending);
        const more = newStart > next ? takeChangesUpTo(addDays(newStart, -1)) : null;
        if (more !== null && more !== pending) {
          pending = more;
          continue;
        }
        if (newStart > next) {
          const tEnd = addDays(newStart, -1);
          if (tEnd >= from && next <= to) {
            out.push({ start: next, end: tEnd, nominal: 7, transitional: true, key: `W:${next}` });
          }
        }
        next = newStart;
        break;
      }
      rule = pending;
    } else if (pending !== null) {
      rule = pending;
    }
    start = next;
  }
  return out;
}

export function monthPeriods(from: LocalDate, to: LocalDate): Period[] {
  const out: Period[] = [];
  for (let s = startOfMonth(from); s <= to; s = addMonths(s, 1)) {
    const e = endOfMonth(s);
    const days = Number(e.slice(8, 10));
    out.push({ start: s, end: e, nominal: days, transitional: false, key: `M:${monthKey(s)}` });
  }
  return out;
}

export function dayKey(d: LocalDate): string {
  return `D:${d}`;
}

export function clampRange(
  a: { start: LocalDate; end: LocalDate },
  b: { start: LocalDate; end: LocalDate },
): { start: LocalDate; end: LocalDate } | null {
  const start = a.start >= b.start ? a.start : b.start;
  const end = minDate(a.end, b.end);
  return start <= end ? { start, end } : null;
}
